<script lang="ts" setup>
withDefaults(defineProps<Props>(), {
  orientation: 'horizontal',
  showBookDemo: true,
  showGetStarted: true,
  showSubscribe: false,
})

const guards = useGuards()
const { t } = useI18n()
const { urls } = useAppConfig()
const localePath = useLocalePath()
const router = useRouter()

interface Props {
  orientation?: 'horizontal' | 'vertical'
  showBookDemo?: boolean
  showGetStarted?: boolean
  showSubscribe?: boolean
}

async function onGetFreeCredits() {
  await guards.awaitUser({
    onClose: () => {},
    onSuccess: () => {
      router.push(localePath('settings-api-tokens'))
    },
  })
}

async function onSubscribe() {
  guards.subscriberOnly()
}
</script>

<template>
  <div
    class="flex flex-wrap items-center gap-8 lg:gap-2"
    :class="{
      'flex-col': orientation === 'vertical',
      'flex-row': orientation === 'horizontal',
    }"
  >
    <div
      v-if="showGetStarted"
      class="-mb-[1.25rem] flex flex-col gap-1"
    >
      <AnimatedBorder
        class="rounded-full"
        :duration="2000"
        hoverable
        :opacity="0.7"
        :spread="8"
      >
        <UButton
          class="rounded-full"
          :label="t('buttons.startFreeCredits')"
          size="lg"
          :trailing-icon="getIcon('arrowRight')"
          :ui="{
            icon: {
              base: 'text-indigo-300 dark:text-indigo-500',
            },
          }"
          @click="onGetFreeCredits"
        />
      </AnimatedBorder>

      <p class="mt-1 w-full text-center text-xs text-gray-500 dark:text-gray-400">
        {{ t('buttons.noCreditCardRequired') }}
      </p>
    </div>

    <div
      v-if="showSubscribe"
      class="flex flex-col gap-1"
    >
      <AnimatedBorder
        class="rounded-full"
        :duration="2000"
        hoverable
        :opacity="0.7"
        :spread="8"
      >
        <UButton
          class="rounded-full"
          :label="t('buttons.subscribe')"
          size="lg"
          :trailing-icon="getIcon('arrowRight')"
          :ui="{
            icon: {
              base: 'text-indigo-300 dark:text-indigo-500',
            },
          }"
          @click="onSubscribe"
        />
      </AnimatedBorder>
    </div>

    <UButton
      v-if="showBookDemo"
      :label="t('buttons.bookDemo')"
      size="lg"
      :to="urls.demo"
      :trailing-icon="getIcon('supportDemo')"
      variant="link"
    />
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "buttons": {
      "bookDemo": "Book a 20 minutes demo",
      "noCreditCardRequired": "No credit card required",
      "startFreeCredits": "Get 500 credits for free",
      "subscribe": "Choose a plan"
    }
  },
  "es": {
    "buttons": {
      "bookDemo": "Reservar una demo de 20 minutos",
      "noCreditCardRequired": "No se requiere tarjeta de crédito",
      "startFreeCredits": "Comienza con 500 créditos gratis",
      "subscribe": "Elige un plan"
    }
  },
  "fr": {
    "buttons": {
      "bookDemo": "Réserver une démo de 20 minutes",
      "noCreditCardRequired": "Aucune carte de crédit requise",
      "startFreeCredits": "Commencez avec 500 crédits gratuits",
      "subscribe": "Choisir un plan"
    }
  }
}
</i18n>
